import { Alert, App as AntApp, Button, Form, Input, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';
import { useCreatePlatformAdminMutation } from './hooks';

const { Paragraph } = Typography;

interface AddPlatformAdminModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  name: string;
  email: string;
  password: string;
}

// Диалог «Добавить суперадмина» (M5.4). POST /platform/admins {email,name,password}.
// 409 email_already_taken → наглядный Alert внутри модалки. Образец — InviteUserModal.
export function AddPlatformAdminModal({ open, onClose }: AddPlatformAdminModalProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<FormValues>();
  const mutation = useCreatePlatformAdminMutation();

  const handleClose = (): void => {
    form.resetFields();
    mutation.reset();
    onClose();
  };

  const onSubmit = (values: FormValues): void => {
    mutation.mutate(
      { name: values.name.trim(), email: values.email.trim(), password: values.password },
      {
        onSuccess: () => {
          void message.success(t('platform.admins.add.success'));
          handleClose();
        },
        onError: (err) => {
          if (!(err instanceof ApiError && err.status === 409)) {
            void message.error(err instanceof Error ? err.message : t('errors.generic'));
          }
        },
      },
    );
  };

  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409;

  return (
    <Modal
      title={t('platform.admins.add.title')}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            loading={mutation.isPending}
            onClick={() => form.submit()}
            data-testid="add-admin-submit"
          >
            {t('platform.admins.add.submit')}
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary">{t('platform.admins.add.subtitle')}</Paragraph>
      <Form<FormValues> form={form} layout="vertical" requiredMark={false} onFinish={onSubmit}>
        <Form.Item
          label={t('platform.admins.add.name')}
          name="name"
          rules={[{ required: true, message: t('platform.admins.add.name_required') }]}
        >
          <Input placeholder="Айгерим Нурланова" autoFocus />
        </Form.Item>
        <Form.Item
          label={t('platform.admins.add.email')}
          name="email"
          rules={[
            { required: true, message: t('platform.admins.add.email_required') },
            { type: 'email', message: t('platform.admins.add.email_invalid') },
          ]}
        >
          <Input placeholder="name@starling.kz" />
        </Form.Item>
        <Form.Item
          label={t('platform.admins.add.password')}
          name="password"
          rules={[
            { required: true, message: t('platform.admins.add.password_required') },
            { min: 8, message: t('platform.admins.add.password_min') },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
      {conflict ? (
        <Alert
          type="warning"
          showIcon
          message={t('platform.admins.add.errors.email_already_taken')}
        />
      ) : mutation.error ? (
        <ErrorAlert error={mutation.error} />
      ) : null}
    </Modal>
  );
}
